import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Plus, Trash, X, Truck as TruckIcon, Drop } from "@phosphor-icons/react";
import { formatDate } from "../lib/format";
import { useCountry, countryInfo } from "../context/CountryContext";

const COUNTRY_OPTIONS = ["GH", "TG", "BJ"];
const ASSET_TYPES = [
  { v: "truck", label: "Truck", hint: "Moves on the road (default)" },
  { v: "station", label: "Yard equipment", hint: "Washing station, container handler…" },
];

export default function Trucks() {
  const { filterParam, defaultCountry } = useCountry();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [prod, setProd] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ vehicle_number: "", license_plate: "", imei: "", capacity_litres: "", notes: "", country: defaultCountry, asset_type: "truck", unit_label: "" });
  const [err, setErr] = useState("");

  const load = async () => {
    const params = filterParam ? { country: filterParam } : {};
    const [t, p] = await Promise.all([
      api.get("/trucks", { params }),
      api.get("/activities/productivity", { params }).catch(() => ({ data: null })),
    ]);
    setRows(t.data);
    setProd(p.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterParam]);

  // Map vehicle_number → trips count
  const tripsByTruck = useMemo(() => {
    const m = {};
    for (const r of prod?.by_truck || []) m[r.vehicle_number] = r;
    return m;
  }, [prod]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/trucks", {
        vehicle_number: form.vehicle_number,
        license_plate: form.license_plate ? form.license_plate.trim() : null,
        imei: form.imei ? form.imei.trim() : null,
        capacity_litres: form.capacity_litres ? parseFloat(form.capacity_litres) : null,
        notes: form.notes,
        country: form.country || "GH",
        asset_type: form.asset_type || "truck",
        unit_label: (form.unit_label || "").trim() || null,
      });
      setOpen(false);
      setForm({ vehicle_number: "", license_plate: "", imei: "", capacity_litres: "", notes: "", country: defaultCountry, asset_type: "truck", unit_label: "" });
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this truck?")) return;
    await api.delete(`/trucks/${id}`);
    await load();
  };

  return (
    <div data-testid="trucks-page">
      <PageHeader
        subtitle="Fleet registry"
        title="Trucks"
        action={
          <button onClick={() => setOpen(true)} data-testid="new-truck-button" className="bg-ps-blue text-white px-4 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-ps-deep transition-colors shadow-sm">
            <Plus size={16} weight="bold" /> Register truck
          </button>
        }
      />

      <div className="p-8 space-y-8">
        {/* Fleet — real trucks */}
        <TruckSection
          title="Fleet"
          subtitle="Vehicles on the road"
          testid="trucks-section-fleet"
          rows={rows.filter((r) => (r.asset_type || "truck") === "truck")}
          tripsByTruck={tripsByTruck}
          prod={prod}
          onDelete={del}
          onOpen={(id) => navigate(`/trucks/${id}`)}
        />

        {/* Yard equipment — stations (washing, Kalmar, generators…) */}
        {rows.some((r) => r.asset_type === "station") && (
          <TruckSection
            title="Yard equipment"
            subtitle="Fuel-draining assets that never move"
            testid="trucks-section-stations"
            rows={rows.filter((r) => r.asset_type === "station")}
            tripsByTruck={{}}
            prod={null}
            onDelete={del}
            onOpen={(id) => navigate(`/trucks/${id}`)}
            variant="station"
          />
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center" onClick={() => setOpen(false)} data-testid="truck-dialog">
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white border border-slate-200">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-heading font-black text-xl tracking-tight">Register truck</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-500"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Vehicle number *" value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })} testid="truck-number" required placeholder="GR 7308 · PST001 · PSB002" />
              <Field label="License plate (optional)" value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: e.target.value })} testid="truck-license-plate" placeholder="TG 7891BF · CE 6689RB" />
              <Field label="GPS IMEI (optional)" value={form.imei} onChange={(e) => setForm({ ...form, imei: e.target.value })} testid="truck-imei" placeholder="15-digit Sinotrack/GPS device ID" />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Fleet origin *</label>
                <div className="grid grid-cols-3 gap-2" data-testid="truck-country-toggle">
                  {COUNTRY_OPTIONS.map((c) => {
                    const info = countryInfo(c);
                    const active = form.country === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setForm({ ...form, country: c })}
                        data-testid={`truck-country-${c}`}
                        className={`border rounded-md px-3 py-2 text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                          active ? "border-ps-blue bg-sky-50 text-ps-deep" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <span>{info.flag}</span>{info.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <Field label="Capacity (L)" type="number" value={form.capacity_litres} onChange={(e) => setForm({ ...form, capacity_litres: e.target.value })} testid="truck-capacity" />

              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Asset type</label>
                <div className="grid grid-cols-2 gap-2" data-testid="truck-asset-type-toggle">
                  {ASSET_TYPES.map((a) => {
                    const active = (form.asset_type || "truck") === a.v;
                    return (
                      <button
                        key={a.v}
                        type="button"
                        onClick={() => setForm({ ...form, asset_type: a.v })}
                        data-testid={`truck-asset-${a.v}`}
                        className={`text-left border rounded-md px-3 py-2 text-sm transition-colors ${active ? "border-ps-blue bg-sky-50 text-ps-deep" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}
                      >
                        <div className="font-semibold">{a.label}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{a.hint}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {form.asset_type === "station" && (
                <Field
                  label="Unit label (e.g. washes, moves)"
                  value={form.unit_label}
                  onChange={(e) => setForm({ ...form, unit_label: e.target.value })}
                  testid="truck-unit-label"
                  placeholder="washes · moves · hours"
                />
              )}

              <Field label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} testid="truck-notes" />
              {err ? <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2">{err}</div> : null}
            </div>
            <div className="p-6 border-t border-slate-200 flex gap-3">
              <button type="button" onClick={() => setOpen(false)} className="flex-1 border border-slate-300 py-2.5 text-sm hover:bg-slate-100">Cancel</button>
              <button type="submit" data-testid="truck-submit" className="flex-1 bg-ps-blue text-white py-2.5 rounded-md text-sm font-semibold hover:bg-ps-deep transition-colors">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function TruckSection({ title, subtitle, testid, rows, tripsByTruck, prod, onDelete, onOpen, variant }) {
  const isStation = variant === "station";
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={testid}>
      <div className="px-6 py-3 border-b border-slate-200 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">{title}</div>
          <div className="text-xs text-slate-500">{subtitle} · {rows.length} {rows.length === 1 ? "item" : "items"}</div>
        </div>
        {!isStation && prod && prod.total_trips > 0 && (
          <div className="text-[10px] uppercase tracking-[0.25em] text-ps-blue font-semibold" data-testid="trucks-prod-summary">
            <span className="text-slate-500">trips this month:</span> <span className="font-mono-data text-ps-deep tracking-normal">{prod.total_trips}</span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200" data-testid={`${testid}-grid`}>
        {rows.map((t) => {
          const stat = tripsByTruck[t.vehicle_number];
          return (
            <div key={t.id} className="bg-white p-4 flex items-center justify-between hover:bg-slate-50 group" data-testid={`truck-card-${t.id}`}>
              <button
                type="button"
                onClick={() => onOpen && onOpen(t.id)}
                data-testid={`truck-open-${t.id}`}
                className="flex items-center gap-3 min-w-0 flex-1 text-left"
              >
                <div className={`w-10 h-10 grid place-items-center rounded-md shrink-0 transition-colors ${isStation ? "bg-lime-100 text-lime-700 group-hover:bg-lime-200" : "bg-ps-blue text-white group-hover:bg-ps-deep"}`}>
                  {isStation ? <Drop size={18} weight="duotone" /> : <TruckIcon size={18} weight="duotone" />}
                </div>
                <div className="min-w-0">
                  <div className="font-mono-data text-base font-semibold tracking-wide flex items-center gap-2 flex-wrap group-hover:text-ps-deep">
                    {t.vehicle_number}
                    <span className="text-base leading-none" title={countryInfo(t.country).name}>{countryInfo(t.country).flag}</span>
                    {isStation && t.unit_label && (
                      <span className="text-[10px] font-mono-data tracking-normal text-lime-700 bg-lime-50 px-1.5 py-0.5 rounded border border-lime-200" title="Unit label">
                        {t.unit_label}
                      </span>
                    )}
                    {t.license_plate && (
                      <span className="text-[10px] font-mono-data tracking-normal text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200" title="License plate" data-testid={`truck-${t.id}-license-plate`}>
                        {t.license_plate}
                      </span>
                    )}
                    {t.imei && (
                      <span className="text-[10px] font-mono-data tracking-normal text-ps-blue bg-sky-50 px-1.5 py-0.5 rounded border border-sky-200" title={`GPS IMEI ${t.imei}`} data-testid={`truck-${t.id}-imei`}>
                        GPS
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    {isStation ? (
                      <>{t.notes ? <span className="normal-case tracking-normal text-slate-500">{t.notes}</span> : <>since {formatDate(t.created_at)}</>}</>
                    ) : stat ? (
                      <>
                        <span className="text-ps-blue font-bold">{stat.trips} trips</span>
                        <span className="text-slate-400 mx-1">·</span>
                        <span className="font-mono-data tracking-normal">{stat.fuel_l} L</span>
                        <span className="text-slate-400 mx-1">this month</span>
                      </>
                    ) : (
                      <>since {formatDate(t.created_at)}</>
                    )}
                  </div>
                </div>
              </button>
              <button onClick={(e) => { e.stopPropagation(); onDelete(t.id); }} data-testid={`delete-truck-${t.id}`} className="text-slate-400 hover:text-red-600 shrink-0 ml-2"><Trash size={14} /></button>
            </div>
          );
        })}
        {rows.length === 0 && <div className="bg-white p-8 text-center text-slate-500 col-span-full">{isStation ? "No yard equipment yet." : "No trucks yet."}</div>}
      </div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{label}</label>
      <input {...props} data-testid={testid} className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 font-mono-data" />
    </div>
  );
}
